import type { MedicineLabelParts } from '@simsm/shared-types';
import { apiClient } from '../../app/api-client';

/**
 * What it takes to render a medicine's identity. `MedicineLabelParts` is the
 * shared contract `formatMedicineLabel` reads (`@simsm/shared-types`), so a hit
 * satisfies the one label function the bff composes with too.
 */
export interface MedicineDisplay extends MedicineLabelParts {
  id: string;
  atcCode: string;
  active: boolean;
  code?: string;
  authorized?: boolean;
}

/**
 * One nomenclator entry. `code` is the key `core.stock.medicine_code` holds and
 * the only value the stock reads accept; `id` identifies the substance and is
 * shared by every code of the same medicine (caiet UC91-02 selects codes).
 */
export interface MedicineHit extends MedicineDisplay {
  code: string;
  authorized: boolean;
  /** caiet UC50 step 4 search fields. '' means the catalog holds no value. */
  manufacturer: string;
  manufacturerCountry: string;
  /** Who holds the authorisation, and where. '' means the catalog holds no value. */
  authorizationHolder: string;
  authorizationHolderCountry: string;
  /** '' for an unauthorized code, which has no authorization to expire. */
  authorizationExpiresOn: string;
}

export interface MedicineSearchResponse {
  items: MedicineHit[];
  total: number;
}

/** Today as a calendar date, the way the catalog stores an expiry. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export interface AuthorizationNote {
  key: 'medicine_combobox.authorization_expires' | 'medicine_combobox.authorization_expired';
  iso: string;
  expired: boolean;
}

/**
 * caiet UC50 step 4 — "Rezultatul include ca informație și data expirării
 * autorizației pentru cele autorizate."
 *
 * `null` means the row says nothing about an authorization, and it is the
 * ordinary case rather than a failure: an unauthorized code has no authorization
 * to expire, and the AMDM authorised feed states an expiry for only part of the
 * register (2128 of 6274 codes; the rest serve the literal string "undefined",
 * spec 022). Never a label with an empty value, never a dash.
 *
 * The two dates are compared as YYYY-MM-DD text. Reading a date-only value into
 * a Date makes it UTC midnight, which moves it to the previous day for every
 * viewer west of Greenwich — on a comparison that decides whether a medicine
 * still has a valid authorization.
 */
export function describeAuthorization(
  hit: Pick<MedicineHit, 'authorized' | 'authorizationExpiresOn'>,
  today: string,
): AuthorizationNote | null {
  if (!hit.authorized || hit.authorizationExpiresOn.length === 0) return null;
  const expired = hit.authorizationExpiresOn < today;
  return {
    key: expired
      ? 'medicine_combobox.authorization_expired'
      : 'medicine_combobox.authorization_expires',
    iso: hit.authorizationExpiresOn,
    expired,
  };
}

/**
 * One fragment of the row's provenance line. `labelKey` names the word that says what
 * the fragment is, and is null for the fragments that need none.
 */
export interface MedicineOriginPart {
  labelKey: string | null;
  value: string;
}

/**
 * The catalog attributes a row shows so the operator can see why it matched.
 *
 * The brand, the producer and the producer's country read unlabelled, in that order: one
 * organisation on the line, and its position says what it is. The authorisation HOLDER is
 * a second organisation, and unlabelled it would leave the reader four proper nouns in a
 * row with no way to tell the producer from the holder — which is why the wireframe's own
 * row labels its metadata (`components/MedicineIdentity.tsx`: Cod, DCI, Producător,
 * Țară). So the holder joins the same line carrying a label, and the four fragments that
 * were already there keep their positions.
 *
 * And it joins it only when it says something new. On 4240 of the 6272 live authorised
 * records with both, the producer string already contains the holder's name, and a second
 * copy of the same organisation is noise on the busiest line in the picker. The test is
 * "does the producer line already say this name" — it never looks for a comma and never
 * decides where a name ends, so it is not the parse spec 022 refused.
 */
export function medicineOrigin(hit: MedicineHit): MedicineOriginPart[] {
  const parts: MedicineOriginPart[] = [
    hit.commercialName,
    hit.manufacturer,
    hit.manufacturerCountry,
  ]
    .filter((part) => part.length > 0)
    .map((value) => ({ labelKey: null, value }));
  if (hit.authorizationHolder.length > 0 && !hit.manufacturer.includes(hit.authorizationHolder)) {
    parts.push({
      labelKey: 'medicine_combobox.origin.holder',
      value: [hit.authorizationHolder, hit.authorizationHolderCountry]
        .filter((part) => part.length > 0)
        .join(', '),
    });
  }
  return parts;
}

/**
 * One unique medicine in a result (caiet §Termeni: DCI, form, dose, volume and
 * division; CF-34). `hit` is the first, best-ranked code of the group with the
 * brand taken off, because the brand belongs to one code and not to the medicine;
 * `codes` are all the codes the search returned for it, in rank order.
 */
export interface UniqueMedicineGroup {
  hit: MedicineHit;
  codes: string[];
  /**
   * The hits behind `codes`, same order. The authorization is a property of the code,
   * not of the unique medicine (caiet UC50 step 4), so the grouped row keeps each
   * member to print every code's own expiry.
   */
  members: MedicineHit[];
}

function uniqueMedicineKey(hit: MedicineHit): string {
  return [hit.dci, hit.pharmaceuticalForm, hit.dose, hit.volume, hit.division]
    .map((part) => part.trim().toLowerCase())
    .join('\u0001');
}

/**
 * Collapses a result of codes into its unique medicines, keeping the rank order of
 * the first code of each. The server resolves any one code of a unique medicine to
 * all of them (docs/shape-ops-unique-medicine.md D3), so the first code is the pick.
 */
export function groupByUniqueMedicine(hits: MedicineHit[]): UniqueMedicineGroup[] {
  const groups = new Map<string, UniqueMedicineGroup>();
  for (const hit of hits) {
    const key = uniqueMedicineKey(hit);
    const group = groups.get(key);
    if (group) {
      group.codes.push(hit.code);
      group.members.push(hit);
    } else {
      groups.set(key, { hit: { ...hit, commercialName: '' }, codes: [hit.code], members: [hit] });
    }
  }
  return [...groups.values()];
}

/** How many codes a grouped search reads, so the codes of one medicine are not cut by the cap. */
export const GROUPED_SEARCH_LIMIT = 100;

export async function searchMedicines(q: string, limit = 8): Promise<MedicineSearchResponse> {
  if (!q || q.trim().length < 2) return { items: [], total: 0 };
  const { data } = await apiClient.get<MedicineSearchResponse>('/catalog/medicines/search', {
    params: { q: q.trim(), limit, onlyActive: true },
  });
  return data;
}
